/**
 * Dragonfly User Service
 * Handles recognition and seamless experience for users coming from any Dragonfly-based platform:
 * - beta.redheli.com
 * - app.redheli.com  
 * - ga.redheli.com
 * - alerislife.com
 */

import { supabase } from '../lib/supabase';

export interface DragonflyUserInfo {
  email: string;
  isDragonflyUser: boolean;
  sourcePlatform?: 'beta' | 'app' | 'ga' | 'aleris' | 'unknown';
  hasCompletedJourney?: boolean;
  journeyProgress?: number;
  lastVisitDate?: string;
  welcomeMessage?: string;
}

export class DragonflyUserService {
  // All Dragonfly platform URLs
  private static dragonflyPlatforms = {
    beta: import.meta.env.VITE_BETA_URL || 'https://beta.redheli.com',
    app: 'https://app.redheli.com',
    ga: 'https://ga.redheli.com',
    aleris: 'https://alerislife.com'
  };
  
  /**
   * Check if user is coming from any Dragonfly platform
   */
  static async checkDragonflyUser(email: string): Promise<DragonflyUserInfo> {
    // Check URL referrer
    const referrer = document.referrer;
    let sourcePlatform: 'beta' | 'app' | 'ga' | 'aleris' | 'unknown' = 'unknown';
    
    // Check which platform they're coming from
    if (referrer.includes('beta.redheli.com')) {
      sourcePlatform = 'beta';
    } else if (referrer.includes('app.redheli.com')) {
      sourcePlatform = 'app';
    } else if (referrer.includes('ga.redheli.com')) {
      sourcePlatform = 'ga';
    } else if (referrer.includes('alerislife.com')) {
      sourcePlatform = 'aleris';
    }
    
    // Check localStorage for any Dragonfly platform history
    const dragonflyHistory = localStorage.getItem('dragonfly_user');
    const lastPlatform = localStorage.getItem('dragonfly_platform') as any;
    const hasDragonflyHistory = dragonflyHistory === email;
    
    // Check for platform-specific cookies
    const hasDragonflySession = 
      document.cookie.includes('beta_session') ||
      document.cookie.includes('app_session') ||
      document.cookie.includes('aleris_session') ||
      document.cookie.includes('dragonfly_session');
    
    // Try to fetch journey status from the appropriate platform
    let journeyData = null;
    if (sourcePlatform !== 'unknown' || lastPlatform) {
      const platform = sourcePlatform !== 'unknown' ? sourcePlatform : lastPlatform;
      journeyData = await DragonflyUserService.fetchJourneyStatus(email, platform);
    }
    
    // Check URL params for platform source
    const urlParams = new URLSearchParams(window.location.search);
    const sourceParam = urlParams.get('source');
    if (sourceParam && ['beta', 'app', 'ga', 'aleris'].includes(sourceParam)) {
      sourcePlatform = sourceParam as any;
    }
    
    const isDragonflyUser = 
      sourcePlatform !== 'unknown' || 
      hasDragonflyHistory || 
      hasDragonflySession || 
      !!journeyData;
    
    // Generate personalized welcome message
    let welcomeMessage = '';
    if (isDragonflyUser) {
      const platformName = DragonflyUserService.getPlatformDisplayName(sourcePlatform || lastPlatform);
      
      if (journeyData?.completed) {
        welcomeMessage = `Welcome back! You've completed your journey on ${platformName}. Explore our AI-powered features to continue your growth.`;
      } else if (journeyData?.progress) {
        welcomeMessage = `Welcome back from ${platformName}! Your journey continues here with enhanced AI capabilities (${Math.round(journeyData.progress * 100)}% complete).`;
      } else {
        welcomeMessage = `Welcome back from ${platformName}! Experience your Red Helicopter journey with our new AI-native features.`;
      }
    }
    
    return {
      email,
      isDragonflyUser,
      sourcePlatform: sourcePlatform !== 'unknown' ? sourcePlatform : lastPlatform,
      hasCompletedJourney: journeyData?.completed || false,
      journeyProgress: journeyData?.progress || 0,
      lastVisitDate: journeyData?.lastVisit,
      welcomeMessage
    };
  }
  
  /**
   * Fetch journey status from a specific Dragonfly platform
   */
  private static async fetchJourneyStatus(email: string, platform: string): Promise<any> {
    try {
      const platformUrl = DragonflyUserService.dragonflyPlatforms[platform as keyof typeof DragonflyUserService.dragonflyPlatforms];
      if (!platformUrl) return null;
      
      const response = await fetch(`${platformUrl}/api/user/status?email=${encodeURIComponent(email)}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-Source': 'hover',
          'X-Request-Platform': platform
        },
        credentials: 'include'
      });
      
      if (response.ok) {
        return await response.json();
      }
    } catch (error) {
      console.log(`Could not fetch journey status from ${platform}:`, error);
    }
    return null;
  }
  
  /**
   * Get display name for platform
   */
  private static getPlatformDisplayName(platform: string): string {
    const names = {
      beta: 'Beta',
      app: 'Red Helicopter',
      ga: 'Red Helicopter GA',
      aleris: 'Aleris Life',
      unknown: 'your previous platform'
    };
    return names[platform as keyof typeof names] || 'Red Helicopter';
  }
  
  /**
   * Store Dragonfly user info for future recognition
   */
  storeDragonflyUserInfo(email: string, platform: string): void {
    localStorage.setItem('dragonfly_user', email);
    localStorage.setItem('dragonfly_platform', platform);
    localStorage.setItem('dragonfly_last_visit', new Date().toISOString());
  }
  
  /**
   * Get personalized onboarding flow for Dragonfly users
   */
  getDragonflyUserOnboarding(userInfo: DragonflyUserInfo): {
    skipIntro: boolean;
    startPath: string;
    features: string[];
    message?: string;
  } {
    // Users from Aleris get special treatment
    if (userInfo.sourcePlatform === 'aleris') {
      return {
        skipIntro: true,
        startPath: '/alerislife',
        features: ['biological-systems', 'ai-coach', 'personalized-insights'],
        message: 'Continue your Aleris Life journey with enhanced AI features'
      };
    }
    
    // Completed journey users
    if (userInfo.hasCompletedJourney) {
      return {
        skipIntro: true,
        startPath: '/portal',
        features: ['ai-coach', 'scenario-analysis', 'community', 'workshop'],
        message: 'Welcome back! Explore advanced features and continue growing'
      };
    }
    
    // In-progress users
    if (userInfo.journeyProgress && userInfo.journeyProgress > 0) {
      return {
        skipIntro: true,
        startPath: '/journey-continue',
        features: ['journey', 'ai-coach', 'progress-tracking'],
        message: 'Continue your journey from where you left off'
      };
    }
    
    // New Dragonfly users get streamlined intro
    return {
      skipIntro: false,
      startPath: '/welcome',
      features: ['journey', 'ai-coach', 'portal'],
      message: 'Begin your enhanced Red Helicopter experience'
    };
  }
  
  /**
   * Sync returning user data with Hover platform
   */
  async syncDragonflyUserData(email: string, platform: string): Promise<void> {
    try {
      // Store in Supabase user metadata
      const { data: { user } } = await supabase.auth.getUser();
      
      if (user) {
        await supabase.auth.updateUser({
          data: {
            returning_user: true,
            source_platform: platform,
            platform_sync_date: new Date().toISOString(),
            platform_migration: 'legacy-to-hover'
          }
        });
      }
      
      // Store locally for quick access
      this.storeDragonflyUserInfo(email, platform);
    } catch (error) {
      console.error('Error syncing returning user data:', error);
    }
  }
  
  /**
   * Handle seamless redirect to appropriate journey
   */
  async handleDragonflyUserJourney(userInfo: DragonflyUserInfo): Promise<string> {
    // If they have query params from Dragonfly, preserve them
    const urlParams = new URLSearchParams(window.location.search);
    const returnUrl = urlParams.get('returnUrl');
    const journeyStep = urlParams.get('step');
    
    // For Aleris users, always go to Aleris page
    if (userInfo.sourcePlatform === 'aleris') {
      return '/alerislife';
    }
    
    // If they're continuing a journey, send them to the right platform
    if (!userInfo.hasCompletedJourney && userInfo.sourcePlatform && userInfo.sourcePlatform !== 'unknown') {
      // Use BetaJourneyService to redirect them back to complete
      const platformUrl = this.dragonflyPlatforms[userInfo.sourcePlatform as keyof typeof this.dragonflyPlatforms];
      const params = new URLSearchParams({
        email: userInfo.email,
        returnUrl: window.location.origin + '/portal',
        source: 'hover',
        continuity: 'true'
      });
      
      if (journeyStep) {
        params.append('step', journeyStep);
      }
      
      return `${platformUrl}/journey?${params.toString()}`;
    }
    
    // Completed users go to portal
    return '/portal';
  }
  
  /**
   * Get quick action buttons for Dragonfly users
   */
  getDragonflyUserActions(userInfo: DragonflyUserInfo): Array<{
    label: string;
    action: string;
    icon: string;
    primary: boolean;
  }> {
    const actions = [];
    
    // Platform-specific actions
    if (userInfo.sourcePlatform === 'aleris') {
      actions.push({
        label: 'Biological Systems',
        action: '/alerislife',
        icon: 'dna',
        primary: true
      });
    }
    
    if (userInfo.hasCompletedJourney) {
      actions.push({
        label: 'AI Portal',
        action: '/portal',
        icon: 'sparkles',
        primary: true
      });
      actions.push({
        label: 'Workshop',
        action: '/workshop',
        icon: 'users',
        primary: false
      });
    } else {
      actions.push({
        label: 'Continue Journey',
        action: 'continue-journey',
        icon: 'compass',
        primary: true
      });
      actions.push({
        label: 'View Progress',
        action: 'journey-progress',
        icon: 'chart',
        primary: false
      });
    }
    
    // Common actions for all
    actions.push({
      label: 'Community',
      action: '/community',
      icon: 'users',
      primary: false
    });
    
    actions.push({
      label: 'Events',
      action: '/events',
      icon: 'calendar',
      primary: false
    });
    
    return actions;
  }
}

// Export singleton instance
export const dragonflyUserService = new DragonflyUserService();